"use client";
import Link from "next/link";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

/** Ordinary anchors work before hydration. Scroll feedback is an enhancement. */
export function PageDirectory({
  items,
  parent,
  current,
}: {
  items: [string, string][];
  parent?: [string, string];
  current?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const site = root.closest<HTMLElement>(".ec-site");
    const header = site?.querySelector<HTMLElement>(".ec-nav");
    const links = [
      ...root.querySelectorAll<HTMLAnchorElement>(".ec-page-links a"),
    ];
    const targets = links.map((link) =>
      document.getElementById(link.hash.slice(1)),
    );
    let frame = 0;
    const measure = () => {
      frame = 0;
      const headerHeight = header ? header.offsetHeight + 24 : 88;
      site?.style.setProperty("--ex-header-offset", `${headerHeight}px`);
      const threshold = headerHeight + root.offsetHeight + 32;
      site?.style.setProperty("--ex-anchor-offset", `${threshold}px`);
      let active = -1;
      targets.forEach((target, index) => {
        if (target && target.getBoundingClientRect().top <= threshold + 8)
          active = index;
      });
      links.forEach((link, index) => {
        if (index === active) link.setAttribute("aria-current", "location");
        else link.removeAttribute("aria-current");
      });
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    const resize = new ResizeObserver(schedule);
    resize.observe(root);
    if (header) resize.observe(header);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    measure();
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      site?.style.removeProperty("--ex-header-offset");
      site?.style.removeProperty("--ex-anchor-offset");
    };
  }, [pathname, items]);
  return (
    <div ref={ref} className="ec-directory ex-directory">
      <div className="ec-container ec-directory-inner">
        {parent && (
          <nav aria-label="Breadcrumb" className="ec-breadcrumb">
            <Link href={parent[1]}>{parent[0]}</Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">{current}</span>
          </nav>
        )}
        <nav aria-label="On this page" className="ec-page-links">
          <span className="ec-eyebrow">On this page</span>
          {items.map(([label, id]) => (
            <a href={`#${id}`} key={id}>
              {label}
            </a>
          ))}
        </nav>
      </div>
    </div>
  );
}
